import type {
	MotornetVehicleResponse,
	MotornetAccessory,
	ColorOption,
	OptionalCatalogItem,
	OptionalCatalogOptions,
	MotornetIdentity,
	SpecItem,
	MotornetVehicleSummary,
} from '../types';
import type { OptionalDTO } from 'src/api';

const priceOf = (a: MotornetAccessory): number | null =>
	a.prezzo ?? a.prezzoListino ?? null;

const dedupeById = <T extends { idAccessorio?: number }>(items: T[]): T[] => {
	const seen = new Set<number>();
	const out: T[] = [];
	for (const item of items) {
		if (item.idAccessorio == null || seen.has(item.idAccessorio)) continue;
		seen.add(item.idAccessorio);
		out.push(item);
	}
	return out;
};

export const motornetToIdentity = (
	payload: MotornetVehicleResponse
): MotornetIdentity => {
	const modello = payload.details?.modello;
	/** Netto  */
	const prezzo_listino = payload.currentPriceDetails?.prezzoListinoNetto ?? 0;
	const displayName = [
		modello?.marca?.nome,
		modello?.modello,
		modello?.allestimento,
	]
		.filter(Boolean)
		.join(' ')
		.trim();
	return {
		codice_costruttore:
			modello?.codiceCostruttore ?? payload.manufactureCode ?? '',
		codice_motornet: modello?.codiceMotornet ?? '',
		codice_eurotax: payload.eurotaxCode ?? modello?.codiceEurotax ?? '',
		prezzo_listino,
		displayName,
	};
};

const norm = (value?: string | null): string => (value ?? '').trim().toLowerCase();

/**
 * Colori esterni: Motornet li identifica tramite il gruppo delle "vernici".
 * `descrizioneGruppo` non serve: vale sempre "Di Serie"/"Optional".
 */
const isPaintAccessory = (a: MotornetAccessory): boolean =>
	[a.macrogruppo, a.descrizioneEquipaggiamento, a.descrizioneNormalizzata].some(
		(field) => norm(field).includes('vernic')
	);

/**
 * Colori interni: stanno nel raggruppamento "Interni" e, al suo interno, sotto
 * due descrizioniNormalizzate:
 * - "Interni%" → tipologia interno, spesso col colore (es. "Interni in pelle nera")
 * - "Personalizzazione colori" → colore interno esplicito a listino (es. "Interni beige")
 * Sui listini reali il raggruppamento non e' sempre il macrogruppo: sulla Fiat 500
 * l'accessorio "Interni bi-colore jacquard" ha macrogruppo "Altro" e
 * descrizioneEquipaggiamento "INTERNI", quindi accettiamo entrambi i segnali
 * (e in fallback la descrizione, che comunque inizia per "Interni").
 */
const isInteriorColorAccessory = (a: MotornetAccessory): boolean => {
	if (!a.codice) return false;
	const groups = [a.macrogruppo].map(norm);
	if (groups.includes('interni')) return true;
	return [a.descrizioneNormalizzata, a.descrizione]
		.map(norm)
		.some(
			(value) =>
				value.startsWith('interni') || value.includes('personalizzazione colori')
		);
};

const toColorOption = (
	a: MotornetAccessory,
	isStandard: boolean
): ColorOption => ({
	value: a.codice,
	label: a.descrizione ? `${a.codice} · ${a.descrizione}` : a.codice,
	description: a.descrizione,
	price: priceOf(a),
	idDizionario: a.idDizionario ?? null,
	idAccessorio: a.idAccessorio ?? null,
	isStandard,
});

/**
 * I colori compresi nel prezzo vanno in cima: sono la scelta di default, e
 * cercarli in fondo a un listino di vernici a pagamento non ha senso. L'ordine
 * di deduplica resta quello originale, qui si riordina solo l'uscita.
 */
const standardFirst = (options: ColorOption[]): ColorOption[] => [
	...options.filter((o) => o.isStandard),
	...options.filter((o) => !o.isStandard),
];

/**
 * Colori esterni disponibili: le vernici a pagamento piu' quelle di serie
 * (presenti nel bucket `serie` e comprese nel prezzo di listino).
 */
export const paintOptions = (payload: MotornetVehicleResponse): ColorOption[] => {
	const optional = (payload.accessories?.vernici ?? []).filter(item => !!item.codice);
	const standard = (payload.accessories?.serie ?? []).filter(isPaintAccessory).filter(item => !!item.codice);
	return standardFirst(
		dedupeById([
			...optional.map((a) => ({ ...a, isStandard: false })),
			...standard.map((a) => ({ ...a, isStandard: true })),
		]).map((a) => toColorOption(a, a.isStandard))
	);
};

/**
 * Colori interni disponibili: quelli a listino come optional piu' quelli di serie.
 */
export const interiorColorOptions = (
	payload: MotornetVehicleResponse
): ColorOption[] => {
	const { accessories } = payload;
	const optional = (accessories?.optionals ?? []).filter(
		isInteriorColorAccessory
	);
	const standard = (accessories?.serie ?? []).filter(isInteriorColorAccessory);
	return standardFirst(
		dedupeById([
			...optional.map((a) => ({ ...a, isStandard: false })),
			...standard.map((a) => ({ ...a, isStandard: true })),
		]).map((a) => toColorOption(a, a.isStandard))
	);
};

/**
 * Il codice colore interno e' obbligatorio solo se il listino propone almeno
 * un colore interno: alcuni veicoli commerciali non ne hanno.
 */
export const isInteriorColorRequired = (interiorColors: ColorOption[]): boolean =>
	interiorColors.length > 0;

export const optionalCatalog = (
	payload: MotornetVehicleResponse,
	{ includePackages = true, includeStandard = false }: OptionalCatalogOptions = {}
): OptionalCatalogItem[] => {
	const { accessories } = payload;
	// L'equipaggiamento di serie va in coda: a parita' di id vince la voce a
	// pagamento, che porta il prezzo.
	const merged = [
		...(accessories?.optionals ?? []).map((a) => ({ a, isStandard: false })),
		...(accessories?.vernici ?? []).map((a) => ({ a, isStandard: false })),
		...(includePackages ? accessories?.pacchetti ?? [] : []).map((a) => ({
			a,
			isStandard: false,
		})),
		...(includeStandard ? accessories?.serie ?? [] : []).map((a) => ({
			a,
			isStandard: true,
		})),
	];
	const byId = new Map<number, OptionalCatalogItem>();
	for (const { a, isStandard } of merged) {
		if (a.idAccessorio == null || byId.has(a.idAccessorio)) continue;
		byId.set(a.idAccessorio, {
			idAccessorio: a.idAccessorio,
			idDizionario: a.idDizionario,
			codice: a.codice,
			descrizione: a.descrizione,
			descrizioneEquipaggiamento: a.descrizioneEquipaggiamento,
			descrizioneNormalizzata: a.descrizioneNormalizzata,
			descrizioneBreve: a.descrizioneBreve,
			prezzo: priceOf(a),
			isStandard,
		});
	}
	return [...byId.values()];
};

/**
 * Import Excel: una riga porta solo stringhe, quindi il codice colore va
 * risolto in due passi, gli stessi che fa `applyColorToOptionals` nel form
 * manuale: codice -> `ColorOption.value` nel pool dei colori -> `idAccessorio`
 * -> voce a catalogo.
 */
export interface ColorOptionalIndex {
	/** codice colore normalizzato -> `codice` della voce di listino */
	exterior: Map<string, string>;
	interior: Map<string, string>;
	interiorColorRequired: boolean;
}

const indexColors = (
	pool: ColorOption[],
	byId: Map<number, OptionalCatalogItem>
): Map<string, string> => {
	const out = new Map<string, string>();
	for (const color of pool) {
		if (color.idAccessorio == null || !color.value) continue;
		const item = byId.get(color.idAccessorio);
		if (!item) continue;
		out.set(color.value.trim().toUpperCase(), item.codice || color.value);
	}
	return out;
};

export const colorOptionalIndex = (
	payload: MotornetVehicleResponse
): ColorOptionalIndex => {
	const byId = new Map(
		optionalCatalog(payload, { includePackages: true }).map((item) => [
			item.idAccessorio,
			item,
		])
	);
	const interiorColors = interiorColorOptions(payload);
	return {
		exterior: indexColors(paintOptions(payload), byId),
		interior: indexColors(interiorColors, byId),
		interiorColorRequired: isInteriorColorRequired(interiorColors),
	};
};

export const toOptionalDTO = (item: OptionalCatalogItem): OptionalDTO => ({
	codice_costruttore: item.codice,
	descrizione: item.descrizione,
	valore: item.prezzo ?? 0,
});

const labelOf = (c?: { descrizione?: string } | null): string =>
	c?.descrizione?.trim() ?? '';

export const vehicleSummary = (
	payload: MotornetVehicleResponse
): MotornetVehicleSummary => {
	const m = payload.details?.modello;
	const id = motornetToIdentity(payload);
	const nfInt = new Intl.NumberFormat('it-IT');
	const specs: SpecItem[] = [];
	const push = (key: string, value: string | number | null | undefined) => {
		if (value === null || value === undefined || value === '') return;
		specs.push({ key, value: String(value) });
	};

	if (m) {
		push('alimentazione', labelOf(m.alimentazione));
		const cambioName =
			m.nomeCambio && m.nomeCambio !== labelOf(m.cambio)
				? ` (${m.nomeCambio})`
				: '';
		push(
			'cambio',
			labelOf(m.cambio) ? `${labelOf(m.cambio)}${cambioName}` : ''
		);
		push('trazione', labelOf(m.trazione) || m.nomeTrazione || '');
		const cv = m.cavalliTotale ?? m.hp;
		const kw = m.potenzaTotale ?? m.kw;
		push(
			'potenza',
			[cv ? `${cv} cv` : '', kw ? `${kw} kW` : ''].filter(Boolean).join(' · ')
		);
		if (m.cilindrata) push('cilindrata', `${nfInt.format(m.cilindrata)} cc`);
		push('porte', m.porte);
		push('posti', m.posti);
		if (m.emissioniCo2) push('co2', `${m.emissioniCo2} g/km`);
		push('carrozzeria', m.nomeCarrozzeria || labelOf(m.tipo));
		push('segmento', labelOf(m.segmento));
	}

	return {
		title: id.displayName,
		codiceCostruttore: id.codice_costruttore,
		codiceMotornet: id.codice_motornet,
		codiceEurotax: id.codice_eurotax,
		prezzoListino: id.prezzo_listino,
		specs,
	};
};
